import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAgentAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  LogIn,
  ShieldCheck,
  Eye,
  EyeOff,
  User2,
  Lock,
  Sparkles,
  Wallet,
  ArrowUpFromLine,
  Headphones,
  Zap,
  Smartphone,
  KeyRound,
  CheckCircle2,
  Lock as LockMini,
  Award,
  ArrowRight,
} from "lucide-react";

const PERKS = [
  { icon: Wallet, title: "Instant Deposits", desc: "Top up any player wallet in seconds" },
  { icon: ArrowUpFromLine, title: "Code-Verified Payouts", desc: "Approve withdrawals with a 6-digit user code" },
  { icon: Zap, title: "Earn Commission", desc: "Get a cut on every deposit & approved withdraw" },
  { icon: Headphones, title: "24/7 Live Support", desc: "Bangla & English priority assistance" },
];

const TRUST_BADGES = [
  { icon: ShieldCheck, label: "Anjouan Licensed" },
  { icon: LockMini, label: "256-bit SSL" },
  { icon: Award, label: "Verified Agents" },
];

export default function AgentLogin() {
  const { agent, login, verify2FA, initializing } = useAgentAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [perkIdx, setPerkIdx] = useState(0);
  // 2FA challenge state
  const [challenge, setChallenge] = useState(null); // {challenge_token, username}
  const [totp, setTotp] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const [backupCode, setBackupCode] = useState("");

  useEffect(() => {
    const t = setInterval(() => setPerkIdx((i) => (i + 1) % PERKS.length), 3500);
    return () => clearInterval(t);
  }, []);

  if (!initializing && agent) return <Navigate to="/agent/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) return toast.error("Username & password required");
    setBusy(true);
    try {
      const res = await login(username.trim(), password);
      if (res?.requires_2fa) {
        setChallenge({ challenge_token: res.challenge_token, username: res.username || username });
        toast.info("Enter the 6-digit code from your authenticator");
      } else {
        toast.success("Welcome to the Agent Panel");
        navigate("/agent/dashboard");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const submit2FA = async (e) => {
    e.preventDefault();
    const code = useBackup ? backupCode.trim() : totp.trim();
    if (!code) return toast.error(useBackup ? "Enter a backup code" : "Enter the 6-digit code");
    setBusy(true);
    try {
      await verify2FA({
        challenge_token: challenge.challenge_token,
        totp_code: useBackup ? undefined : code,
        backup_code: useBackup ? code : undefined,
      });
      toast.success("Welcome to the Agent Panel");
      navigate("/agent/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid code — try again");
    } finally {
      setBusy(false);
    }
  };

  const cancel2FA = () => {
    setChallenge(null);
    setTotp("");
    setBackupCode("");
    setUseBackup(false);
  };

  return (
    <div
      className="min-h-screen text-white flex relative overflow-hidden"
      data-testid="agent-login-page"
      style={{
        background:
          "radial-gradient(1100px 760px at 0% 0%, rgba(16,185,129,0.16), transparent 60%)," +
          "radial-gradient(900px 620px at 100% 100%, rgba(245,158,11,0.11), transparent 60%)," +
          "radial-gradient(700px 700px at 50% 40%, rgba(56,189,248,0.06), transparent 60%)," +
          "linear-gradient(180deg, #03060c 0%, #050a13 60%, #04080f 100%)",
      }}
    >
      {/* ===== Animated grid ===== */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.055]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.7) 1px, transparent 1px),linear-gradient(90deg, rgba(255,255,255,.7) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage: "radial-gradient(ellipse at center, black 30%, transparent 78%)",
        }}
      />

      {/* ===== Floating orbs (animated) ===== */}
      <div
        aria-hidden
        className="absolute top-[8%] -left-[6%] h-[460px] w-[460px] rounded-full bg-emerald-500/20 blur-[130px]"
        style={{ animation: "ml88-float 14s ease-in-out infinite" }}
      />
      <div
        aria-hidden
        className="absolute bottom-[4%] -right-[6%] h-[420px] w-[420px] rounded-full bg-amber-500/12 blur-[130px]"
        style={{ animation: "ml88-float 18s ease-in-out infinite 2s" }}
      />
      <div
        aria-hidden
        className="absolute top-[40%] left-[40%] h-[260px] w-[260px] rounded-full bg-sky-500/10 blur-[110px]"
        style={{ animation: "ml88-float 22s ease-in-out infinite 4s" }}
      />

      {/* Inline keyframes (no tailwind config edit) */}
      <style>{`
        @keyframes ml88-float {
          0%,100% { transform: translate3d(0,0,0) scale(1); opacity: .9; }
          50%     { transform: translate3d(40px,-30px,0) scale(1.08); opacity: 1; }
        }
        @keyframes ml88-shimmer {
          0% { transform: translateX(-150%); }
          100% { transform: translateX(150%); }
        }
        @keyframes ml88-ring {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes ml88-rise {
          0% { opacity: 0; transform: translateY(14px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .ml88-rise { animation: ml88-rise .55s cubic-bezier(.2,.7,.2,1) both; }
        .ml88-logo-ring::before {
          content: "";
          position: absolute;
          inset: -6px;
          border-radius: 22px;
          background: conic-gradient(from 0deg, rgba(16,185,129,0) 0deg, rgba(52,211,153,.55) 90deg, rgba(245,158,11,.45) 200deg, rgba(16,185,129,0) 360deg);
          filter: blur(6px);
          z-index: -1;
          animation: ml88-ring 6s linear infinite;
        }
      `}</style>

      {/* ===== LEFT: Brand showcase (desktop only) ===== */}
      <div className="hidden lg:flex flex-col justify-between w-[55%] p-12 xl:p-16 relative">
        <div>
          <div className="inline-flex items-center gap-3 ml88-rise">
            <div className="relative ml88-logo-ring">
              <div
                className="h-14 w-14 rounded-2xl grid place-items-center font-black text-[#06140b] text-2xl relative z-10"
                style={{
                  background: "linear-gradient(135deg,#34d399,#059669)",
                  boxShadow: "0 22px 55px -10px rgba(16,185,129,0.65), inset 0 -3px 0 rgba(0,0,0,.18)",
                }}
              >
                M
              </div>
              <span className="absolute -bottom-1.5 -right-1.5 h-5 w-5 rounded-full bg-amber-400 border-[3px] border-[#04070d] grid place-items-center z-20">
                <Sparkles size={9} className="text-[#1a1208]" strokeWidth={3} />
              </span>
            </div>
            <div className="leading-tight">
              <div className="text-3xl font-black tracking-tight">Maxlive88</div>
              <div className="text-[11px] uppercase tracking-[2.5px] text-emerald-400 font-bold">Agent Network</div>
            </div>
          </div>

          <div className="mt-12 max-w-lg ml88-rise" style={{ animationDelay: ".08s" }}>
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[2px] text-emerald-300 border border-emerald-500/30 bg-emerald-500/10 rounded-full px-3 py-1.5">
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
              </span>
              Authorized Partner Access
            </span>
            <h1 className="mt-5 text-[44px] xl:text-[54px] font-black tracking-[-0.02em] leading-[1.04]">
              Power the<br />
              <span className="bg-gradient-to-r from-emerald-300 via-amber-300 to-emerald-400 bg-clip-text text-transparent">
                Maxlive88 economy.
              </span>
            </h1>
            <p className="mt-5 text-white/60 text-[15px] leading-relaxed max-w-md">
              Move funds, approve withdrawals, and earn commission on every transaction —
              all from one fast, beautifully simple dashboard.
            </p>
          </div>

          {/* Perks rotator */}
          <div className="mt-10 max-w-md ml88-rise" style={{ animationDelay: ".16s" }}>
            <div className="grid grid-cols-2 gap-3">
              {PERKS.map((p, i) => {
                const Icon = p.icon;
                const active = i === perkIdx;
                return (
                  <div
                    key={i}
                    className={`relative overflow-hidden rounded-xl border p-3.5 transition-all duration-500 ${
                      active
                        ? "bg-gradient-to-br from-emerald-500/15 to-emerald-500/5 border-emerald-400/40 shadow-[0_10px_30px_-10px_rgba(16,185,129,0.45)] scale-[1.02]"
                        : "bg-white/[0.02] border-white/[0.06]"
                    }`}
                  >
                    <Icon size={18} className={active ? "text-emerald-300" : "text-white/45"} />
                    <div className={`mt-2 text-[12.5px] font-extrabold tracking-tight ${active ? "text-white" : "text-white/75"}`}>
                      {p.title}
                    </div>
                    <div className="text-[10.5px] text-white/45 mt-0.5 leading-tight">{p.desc}</div>
                    {active && (
                      <span
                        aria-hidden
                        className="absolute top-2 right-2 h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.7)]"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-6 max-w-lg pt-8 border-t border-white/[0.06]">
          <div>
            <div className="text-2xl font-black tracking-tight text-emerald-300">৳120Cr+</div>
            <div className="text-[10.5px] uppercase tracking-[1.5px] text-white/40 mt-0.5 font-semibold">Payouts to date</div>
          </div>
          <div>
            <div className="text-2xl font-black tracking-tight text-amber-300">92K+</div>
            <div className="text-[10.5px] uppercase tracking-[1.5px] text-white/40 mt-0.5 font-semibold">Active players</div>
          </div>
          <div>
            <div className="text-2xl font-black tracking-tight text-sky-300">24/7</div>
            <div className="text-[10.5px] uppercase tracking-[1.5px] text-white/40 mt-0.5 font-semibold">Operations</div>
          </div>
        </div>
      </div>

      {/* ===== RIGHT: Form ===== */}
      <div className="flex-1 flex items-start lg:items-center justify-center px-4 sm:px-8 pt-[max(env(safe-area-inset-top),28px)] pb-[max(env(safe-area-inset-bottom),28px)] relative">
        <div className="relative w-full max-w-[440px]">
          {/* === Mobile brand HERO === */}
          <div className="lg:hidden text-center mb-6 ml88-rise">
            <div className="relative inline-flex flex-col items-center">
              <div className="relative ml88-logo-ring inline-flex">
                <div
                  className="h-[72px] w-[72px] rounded-2xl grid place-items-center font-black text-[#06140b] text-[34px] relative z-10"
                  style={{
                    background: "linear-gradient(135deg,#34d399 0%,#10b981 55%,#047857 100%)",
                    boxShadow:
                      "0 30px 80px -18px rgba(16,185,129,0.7), inset 0 -3px 0 rgba(0,0,0,.18), 0 0 0 1px rgba(52,211,153,.45)",
                  }}
                >
                  M
                </div>
                <span className="absolute -bottom-1.5 -right-1.5 h-6 w-6 rounded-full bg-amber-400 border-[3px] border-[#04070d] grid place-items-center z-20">
                  <Sparkles size={10} className="text-[#1a1208]" strokeWidth={3} />
                </span>
              </div>
              <div className="mt-4">
                <div className="text-[30px] leading-none font-black tracking-tight">
                  Maxlive<span className="bg-gradient-to-r from-emerald-300 to-amber-300 bg-clip-text text-transparent">88</span>
                </div>
                <div className="mt-1.5 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[3px] text-emerald-300/90 font-bold">
                  <span className="h-[6px] w-[6px] rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.9)]" />
                  Agent Panel
                </div>
              </div>
            </div>
          </div>

          {/* === Card with animated gradient border === */}
          <div className="relative ml88-rise" style={{ animationDelay: ".08s" }}>
            <div
              aria-hidden
              className="absolute -inset-px rounded-[22px]"
              style={{
                background:
                  "linear-gradient(135deg, rgba(52,211,153,.55), rgba(255,255,255,.05) 45%, rgba(245,158,11,.45))",
              }}
            />
            {!challenge ? (
              <form
                onSubmit={submit}
                className="relative bg-[#070b14]/95 backdrop-blur-xl rounded-[21px] p-6 sm:p-8"
                data-testid="agent-login-form"
                style={{
                  boxShadow:
                    "0 60px 120px -30px rgba(0,0,0,.6), 0 0 0 1px rgba(255,255,255,.02), inset 0 1px 0 rgba(255,255,255,.04)",
                }}
              >
                <div className="mb-6 sm:mb-7">
                  <h2 className="text-[28px] sm:text-[30px] font-black tracking-tight leading-none">Welcome back</h2>
                  <p className="text-white/55 text-[13.5px] mt-2">Sign in to your agent dashboard.</p>
                </div>

                {/* Username */}
                <label className="block text-[10.5px] uppercase tracking-[1.5px] text-white/55 mb-1.5 font-bold">Username</label>
                <div className="relative group">
                  <User2
                    size={16}
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40 group-focus-within:text-emerald-400 transition"
                  />
                  <input
                    data-testid="agent-login-username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="username"
                    placeholder="Your agent username"
                    className="w-full h-[52px] sm:h-12 pl-10 pr-3.5 bg-white/[0.035] border border-white/10 rounded-xl text-[15px] sm:text-sm focus:border-emerald-500/60 focus:bg-white/[0.06] focus:outline-none focus:ring-4 focus:ring-emerald-500/10 transition"
                  />
                </div>

                {/* Password */}
                <label className="block text-[10.5px] uppercase tracking-[1.5px] text-white/55 mt-5 mb-1.5 font-bold">Password</label>
                <div className="relative group">
                  <Lock
                    size={16}
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40 group-focus-within:text-emerald-400 transition"
                  />
                  <input
                    data-testid="agent-login-password"
                    type={showPwd ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    className="w-full h-[52px] sm:h-12 pl-10 pr-12 bg-white/[0.035] border border-white/10 rounded-xl text-[15px] sm:text-sm focus:border-emerald-500/60 focus:bg-white/[0.06] focus:outline-none focus:ring-4 focus:ring-emerald-500/10 transition"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 h-8 w-8 grid place-items-center rounded-md text-white/45 hover:text-white hover:bg-white/5 transition"
                    data-testid="agent-login-toggle-password"
                    aria-label={showPwd ? "Hide password" : "Show password"}
                  >
                    {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>

                {/* CTA */}
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="agent-login-submit"
                  className="group relative mt-7 w-full h-[54px] sm:h-12 rounded-xl font-extrabold inline-flex items-center justify-center gap-2 disabled:opacity-50 transition-all overflow-hidden active:scale-[0.99]"
                  style={{
                    background: "linear-gradient(135deg,#34d399 0%,#10b981 50%,#059669 100%)",
                    color: "#06140b",
                    boxShadow:
                      "0 22px 50px -12px rgba(16,185,129,0.65), inset 0 -2px 0 rgba(0,0,0,.15), 0 0 0 1px rgba(52,211,153,.4)",
                  }}
                >
                  <span
                    aria-hidden
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(110deg, transparent 35%, rgba(255,255,255,.45) 50%, transparent 65%)",
                      animation: "ml88-shimmer 2.6s linear infinite",
                    }}
                  />
                  <LogIn size={17} className="relative" strokeWidth={2.6} />
                  <span className="relative text-[15px] tracking-wide">
                    {busy ? "Signing in…" : "Sign in to dashboard"}
                  </span>
                  {!busy && <ArrowRight size={16} className="relative opacity-80 group-hover:translate-x-0.5 transition-transform" strokeWidth={2.6} />}
                </button>

                {/* Security note */}
                <div className="mt-5 flex items-start gap-2 text-[11.5px] text-white/55 bg-emerald-500/[0.04] border border-emerald-500/15 rounded-xl px-3 py-2.5">
                  <ShieldCheck size={14} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>
                    Agent credentials are issued by the admin team. Never share your password — official staff
                    will never ask for it.
                  </span>
                </div>

                {/* Trust badges */}
                <div className="mt-5 grid grid-cols-3 gap-2">
                  {TRUST_BADGES.map((b, i) => {
                    const Icon = b.icon;
                    return (
                      <div
                        key={i}
                        className="flex flex-col items-center justify-center gap-1.5 rounded-lg border border-white/[0.06] bg-white/[0.02] py-2.5"
                      >
                        <Icon size={13} className="text-emerald-300/85" />
                        <div className="text-[9.5px] uppercase tracking-[1px] text-white/55 font-bold text-center leading-tight px-1">
                          {b.label}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </form>
            ) : (
              <form
                onSubmit={submit2FA}
                className="relative bg-[#070b14]/95 backdrop-blur-xl rounded-[21px] p-6 sm:p-8"
                data-testid="agent-login-2fa-form"
                style={{
                  boxShadow:
                    "0 60px 120px -30px rgba(0,0,0,.6), 0 0 0 1px rgba(255,255,255,.02), inset 0 1px 0 rgba(255,255,255,.04)",
                }}
              >
                <div className="mb-6 flex items-center gap-3">
                  <div className="h-12 w-12 rounded-xl grid place-items-center bg-emerald-500/15 border border-emerald-500/30 text-emerald-300">
                    <ShieldCheck size={22} />
                  </div>
                  <div>
                    <h2 className="text-[22px] font-black tracking-tight">Two-factor verification</h2>
                    <p className="text-white/55 text-[12.5px] mt-0.5">
                      Hi <b className="text-white/85">{challenge.username}</b> — enter the 6-digit code from your authenticator.
                    </p>
                  </div>
                </div>

                {!useBackup ? (
                  <>
                    <label className="block text-[10.5px] uppercase tracking-[1.5px] text-white/55 mb-1.5 font-bold">
                      Authenticator code
                    </label>
                    <div className="relative">
                      <Smartphone size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
                      <input
                        data-testid="agent-login-2fa-code"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={totp}
                        onChange={(e) => setTotp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        autoFocus
                        placeholder="000000"
                        maxLength={6}
                        className="w-full h-16 pl-10 pr-3.5 bg-white/[0.035] border border-white/10 rounded-xl text-center text-2xl tracking-[0.5em] font-mono focus:border-emerald-500/60 focus:bg-white/[0.06] focus:outline-none focus:ring-4 focus:ring-emerald-500/10 transition"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <label className="block text-[10.5px] uppercase tracking-[1.5px] text-white/55 mb-1.5 font-bold">
                      Backup code
                    </label>
                    <div className="relative">
                      <KeyRound size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
                      <input
                        data-testid="agent-login-2fa-backup"
                        type="text"
                        value={backupCode}
                        onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
                        autoFocus
                        placeholder="XXXXX-XXXXX"
                        className="w-full h-[52px] pl-10 pr-3.5 bg-white/[0.035] border border-white/10 rounded-xl text-sm font-mono focus:border-emerald-500/60 focus:bg-white/[0.06] focus:outline-none focus:ring-4 focus:ring-emerald-500/10 transition"
                      />
                    </div>
                  </>
                )}

                <button
                  type="submit"
                  disabled={busy || (!useBackup && totp.length !== 6) || (useBackup && !backupCode)}
                  data-testid="agent-login-2fa-submit"
                  className="group relative mt-6 w-full h-[54px] sm:h-12 rounded-xl font-extrabold inline-flex items-center justify-center gap-2 disabled:opacity-50 transition-all overflow-hidden active:scale-[0.99]"
                  style={{
                    background: "linear-gradient(135deg,#34d399 0%,#10b981 50%,#059669 100%)",
                    color: "#06140b",
                    boxShadow: "0 22px 50px -12px rgba(16,185,129,0.6)",
                  }}
                >
                  <CheckCircle2 size={17} className="relative" strokeWidth={2.5} />
                  <span className="relative text-[15px] tracking-wide">{busy ? "Verifying…" : "Verify & sign in"}</span>
                </button>

                <div className="mt-4 flex items-center justify-between text-[11.5px]">
                  <button
                    type="button"
                    onClick={() => {
                      setUseBackup(!useBackup);
                      setTotp("");
                      setBackupCode("");
                    }}
                    data-testid="agent-login-2fa-toggle-backup"
                    className="text-emerald-300 hover:text-emerald-200 font-semibold"
                  >
                    {useBackup ? "Use authenticator code instead" : "Use a backup code instead"}
                  </button>
                  <button type="button" onClick={cancel2FA} className="text-white/50 hover:text-white/75">
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>

          <p className="text-center text-[10.5px] text-white/35 mt-5 font-medium px-3 leading-relaxed">
            © 2026 Maxlive88 · Anjouan licensed (ALSI-202410030-FI1)
          </p>
        </div>
      </div>
    </div>
  );
}
